import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Wind, Droplets } from "lucide-react";
import { api } from "../api";
import { useI18n, useL } from "../i18n";

// Open-Meteo weather-code → icon + label
function codeToIcon(code) {
  if (code === 0) return { Icon: Sun, ar: "صافي", en: "Clear", fr: "Ciel clair" };
  if (code >= 1 && code <= 3) return { Icon: Cloud, ar: "غيوم متفرقة", en: "Cloudy", fr: "Nuageux" };
  if (code >= 45 && code <= 48) return { Icon: CloudFog, ar: "ضباب", en: "Fog", fr: "Brouillard" };
  if (code >= 51 && code <= 67) return { Icon: CloudRain, ar: "أمطار", en: "Rain", fr: "Pluie" };
  if (code >= 71 && code <= 77) return { Icon: CloudSnow, ar: "ثلوج", en: "Snow", fr: "Neige" };
  if (code >= 80 && code <= 82) return { Icon: CloudRain, ar: "زخات مطر", en: "Showers", fr: "Averses" };
  if (code >= 95) return { Icon: CloudLightning, ar: "عواصف رعدية", en: "Thunderstorm", fr: "Orage" };
  return { Icon: Sun, ar: "معتدل", en: "Mild", fr: "Doux" };
}

export default function WeatherBadge({ destId }) {
  const { lang } = useI18n();
  const L = useL();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    api.get(`/weather/${destId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message));
  }, [destId]);

  if (error || !data) {
    return (
      <div className="rounded-2xl bg-[#F3EDE4] p-4 animate-pulse h-24" data-testid="weather-loading" />
    );
  }

  const cur = data.current || {};
  const { Icon, ar, en, fr } = codeToIcon(cur.weather_code);
  const label = { ar, en, fr }[lang] || en;

  return (
    <div className="rounded-2xl bg-white border border-[#E6DED3] p-4" data-testid="weather-badge">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#FEF3C7] text-[#D97706] grid place-items-center">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#78716C]">
              {L({ ar: "الطقس الآن", en: "Weather now", fr: "Météo actuelle" })}
            </div>
            <div className="font-display text-2xl text-[#1C1917]">
              {Math.round(cur.temperature_2m)}°C
              <span className="ms-2 text-sm text-[#57534E] font-normal">{label}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-[#57534E]">
          <span className="inline-flex items-center gap-1"><Wind className="h-3 w-3" />{Math.round(cur.wind_speed_10m)} km/h</span>
          <span className="inline-flex items-center gap-1"><Droplets className="h-3 w-3" />{Math.round(cur.relative_humidity_2m)}%</span>
        </div>
      </div>

      {data.daily?.temperature_2m_max && (
        <div className="mt-3 pt-3 border-t border-[#E6DED3] flex justify-between gap-1 overflow-x-auto scrollbar-none">
          {data.daily.temperature_2m_max.slice(0, 5).map((max, i) => {
            const { Icon: DayIcon } = codeToIcon(data.daily.weather_code[i]);
            const min = data.daily.temperature_2m_min[i];
            const isoDay = data.daily.time?.[i];
            const day = new Date(isoDay || Date.now()).toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-US", { weekday: "short" });
            return (
              <div key={isoDay || `day-${i}`} className="flex flex-col items-center gap-1 min-w-[56px]">
                <div className="text-[10px] text-[#78716C] uppercase tracking-widest">{day}</div>
                <DayIcon className="h-4 w-4 text-[#C85A32]" />
                <div className="text-xs text-[#1C1917] font-medium">{Math.round(max)}°</div>
                <div className="text-[10px] text-[#78716C]">{Math.round(min)}°</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
